'use client';

import { useEffect, useRef, useState } from 'react';
import type { InstantRange, ResourceHistoryModalProps } from 'elowen-plugin-ui-kit';
import { ArrowDownUp, Cpu, HardDrive, MemoryStick, Network, type LucideIcon } from 'lucide-react';
import { METRIC_COLOUR, METRIC_KINDS, historyTime, metricLabel, resourceHistoryChart } from '../../lib/resourceHistoryChart';
import type { ResourceHistoryMetric } from 'elowen-plugin-ui-kit/resourceHistory';
import { Button } from './Button';
import { Checkbox } from './Checkbox';
import { DateRangeFilter } from './DateRangeFilter';
import { Modal, ModalBody } from './Modal';
import { TimeSeriesChart } from './TimeSeriesChart';
import { EmptyState, ErrorState, LoadingState } from './states';
import { apiErrorMessage } from '../../lib/elowenClient';
import { formatBytes } from '../../lib/format';
import { useTranslation } from '../../lib/i18n';
import { resourceMetricStrings } from '../../lib/resourceMetricLabels';
import { useMobileViewport } from '../../lib/useMobile';

const METRIC_ICON: Record<ResourceHistoryMetric, LucideIcon> = { cpu: Cpu, memory: MemoryStick, disk: HardDrive, io: ArrowDownUp, network: Network };
const HISTORY_ERRORS = ['invalid_history_query', 'invalid_history_range', 'range_outside_retention', 'unsupported_history_resolution', 'invalid_history_response'] as const;

/** The one resource-history frame. The caller supplies its authorized reader and optional scope picker. */
export function ResourceHistoryModal({ metric, description, scope, useHistory, onClose, testId = 'resource-history-modal' }: ResourceHistoryModalProps) {
  const { t, locale } = useTranslation();
  const s = t.settings;
  const strings = resourceMetricStrings(t);
  const phone = useMobileViewport();
  const [range, setRange] = useState<InstantRange>({ preset: '24h', from: null, to: null });
  const [metrics, setMetrics] = useState<ResourceHistoryMetric[]>([metric]);
  const history = useHistory({ range, metrics });
  const data = history.data;
  const code = HISTORY_ERRORS.find(value => value === apiErrorMessage(history.error));
  const failure = history.failure ?? (history.error ? code ? s[`error_${code}`] : s.historyError : null);
  const empty = data && (!data.series.some(series => series.points.some(point => point.mean !== null)) || data.range.from === data.range.to);
  const [announcement, setAnnouncement] = useState('');
  const previousData = useRef(data);
  useEffect(() => {
    if (!data || data === previousData.current) return;
    previousData.current = data;
    setAnnouncement(historyTime(Date.parse(data.range.from), locale) + ' – ' + historyTime(Date.parse(data.range.to), locale));
  }, [data, locale]);

  if (phone === undefined) return null;
  return <Modal title={s.historyTitle} description={description} size="page" data-testid={testId}
    presentation={phone ? 'fullscreen' : 'center'} onClose={onClose}>
    <div className="flex min-w-0 flex-col gap-2 border-b border-border px-4 py-3">
      <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
        {scope}
        <div className="flex min-w-0 max-w-full shrink-0">
          <DateRangeFilter precision="instant" value={range} min={history.min} max={history.max} onChange={setRange} />
        </div>
      </div>
      <fieldset className="flex min-w-0 flex-wrap gap-2">
        <legend className="sr-only">{s.historyMetrics}</legend>
        {METRIC_KINDS.map(kind => {
          const checked = metrics.includes(kind);
          const Icon = METRIC_ICON[kind];
          return <Button key={kind} variant="ghost" size="sm" role="checkbox" aria-checked={checked}
            disabled={checked && metrics.length === 1}
            onClick={() => setMetrics(previous => checked ? previous.filter(value => value !== kind) : [...previous, kind])}>
            <Checkbox checked={checked} />
            <Icon size={14} aria-hidden style={{ color: METRIC_COLOUR[kind] }} />
            {metricLabel(kind, strings)}
          </Button>;
        })}
      </fieldset>
    </div>
    <ModalBody>
      <div className="flex min-w-0 flex-1 flex-col gap-4">
        <p className="sr-only" role="status" aria-live="polite">{announcement}</p>
        {failure ? <ErrorState message={failure} onRetry={history.retry} /> : null}
        {data && (data.range.adjusted || history.error) ? <div data-testid="resource-history-notice" className="space-y-1 break-words text-meta text-muted-foreground">
          {data.range.adjusted ? <p>{s.historyAdjusted}</p> : null}
          {history.error ? <p>{s.historyStale}</p> : null}
        </div> : null}
        {history.loading && !failure ? <div className="flex min-h-[min(60dvh,--spacing(140))] flex-1 flex-col">
          <LoadingState variant="block" height="h-full" />
        </div> : data === undefined ? null : empty ? <EmptyState
          title={data.sampledSince === null ? s.historyNotStarted : s.historyEmpty}
          description={s.historyGapsHelp} /> : <section className="flex min-h-[min(60dvh,--spacing(140))] min-w-0 flex-1 flex-col gap-2">
          <p className="text-meta text-muted-foreground">{s.historyShare}</p>
          <TimeSeriesChart {...resourceHistoryChart(data, metrics, strings, locale, formatBytes)} />
        </section>}
      </div>
    </ModalBody>
  </Modal>;
}
